import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { Action } from '../../../typings/permissions';
import { userHasPermissionForProject, userHasPermissionForReleaseGroup } from '../../helpers/permissions';
import ProjectGroupModule from '../../redux/project_grouping';
import TeamsModule from '../../redux/teams';
import { IAsyncStatus, Team } from '../../types';
import { hasError, selectOptionFactory } from '../../utils';
import Button, { ButtonGroup } from '../generic/Button';
import { ErrorAlert } from '../generic/ErrorAlert';
import Loader from '../generic/Loader';
import Select from '../generic/Select/Select';

type ReduxStateProps = {
  status: IAsyncStatus;
  fetchingAllProjectsInGroupStatus: IAsyncStatus;
};

type DispatchProps = {
  fetchAllProjectsInReleaseGroup(groupId: number): Promise<void>;
};

type AddTeamProject = {
  locator: string;
  title: string;
};

type AddTeamReleaseGroup = {
  id: number;
  title: string;
  releases: Array<{
    projects: Array<{
      projectId: string;
    }>;
  }>;
}

type ModalAddTeamProjectsProps = ReduxStateProps & DispatchProps & {
  team: Team;
  projects: AddTeamProject[];
  releaseGroups: AddTeamReleaseGroup[];
  onDone(projectIds: string[], releaseGroups: number[]): void;
  onCancel(): void;
};

type OptionValue = {
  type: 'project';
  id: string;
} | {
  type: 'releaseGroup';
  id: number;
}

type SelectedOption = {
  label: string;
  value: OptionValue;
}

/**
 * The modal for adding project(s) to a team.
 *
 * @param {IAsyncStatus} status - Network/async request status, from Redux.
 * @param {IAsyncStatus} fetchingAllProjectsInGroupStatus - Network/async request status for the projects in groups, from Redux.
 * @param {(groupId: number) => Promise<void>} fetchAllProjectsInReleaseGroup - Method to fetch the projects in groups. Redux action.
 * @param {Team} team - The team we are adding project(s) to.
 * @param {Array<{ locator: string; title: string }>} projects - List of projects.
 * @param {(projectIds: string[], releaseGroups: number[]) => void} releaseGroups - List of release groups.
 * @param {releaseGroups: Array<{ id: number, title: string, releases: Array<{ projects: Array<{ projectId: string, }>, }>, }>} onDone - Method for saving the changes.
 * @param {() => void} onCancel - Method for closing the modal/losing changes.
 */
const ModalAddTeamProjects = (props: ModalAddTeamProjectsProps) => {
  const [selectedOptions, setSelectedOptions] = useState<SelectedOption[]>([]);

  // checkedReleaseGroups will say for which release groups we've fetched all projects to be included among the
  // projects in the team.
  const [checkedReleaseGroups, setCheckedReleaseGroups] = useState<Set<number>>(new Set());

  const hasProjectBeenSelected = (project: AddTeamProject) => selectedOptions.some((selectedOption: SelectedOption) =>
    selectedOption.value.type === 'project' && project.locator === selectedOption.value.id);

  const hasReleaseGroupBeenSelected = (releaseGroup: AddTeamReleaseGroup) => selectedOptions.some((selectedOption: SelectedOption) =>
    selectedOption.value.type === 'releaseGroup' && releaseGroup.id === selectedOption.value.id);

  const isProjectInTeam = (project: AddTeamProject) =>
    props.team.teamProjects?.some(teamProject => teamProject.projectId === project.locator);

  const isReleaseGroupInTeam = (releaseGroup: AddTeamReleaseGroup) =>
    props.team.teamReleaseGroups?.some(teamReleaseGroup => teamReleaseGroup.releaseGroupId === releaseGroup.id);

  const shouldDisplayProject = (project: AddTeamProject) =>
    !isProjectInTeam(project) &&
    !hasProjectBeenSelected(project) &&
    userHasPermissionForProject(Action.Edit, project.locator);

  const shouldDisplayReleaseGroup = (releaseGroup: AddTeamReleaseGroup) =>
    !isReleaseGroupInTeam(releaseGroup) &&
    !hasReleaseGroupBeenSelected(releaseGroup) &&
    userHasPermissionForReleaseGroup(Action.Edit, releaseGroup.id);

  const projectOptions = props.projects
    .filter(shouldDisplayProject)
    .map(project => selectOptionFactory({ type: 'project', id: project.locator }, project.title));

  const releaseGroupOptions = props.releaseGroups
    .filter(shouldDisplayReleaseGroup)
    .map(releaseGroup => selectOptionFactory({ type: 'releaseGroup', id: releaseGroup.id }, releaseGroup.title));

  const modalOptions = [
    ...projectOptions,
    ...releaseGroupOptions,
  ];

  const filterProjectsAndReleaseGroups = (options: Array<{ label: string; value: OptionValue }>) => {
    const projectIds: string[] = [];
    const releaseGroupIds: number[] = [];

    options.forEach(({ value }) => {
      if (value.type === 'project') {
        projectIds.push(value.id);
      } else {
        releaseGroupIds.push(value.id);
      }
    });

    props.onDone(projectIds, releaseGroupIds);
  };

  const updateSelectedOptions = async (options: Array<{ label: string; value: OptionValue }>) => {
    const newCheckedReleaseGroups = new Set<number>();
    for (const { value } of options) {
      if (value.type === 'releaseGroup') {
        newCheckedReleaseGroups.add(value.id);
        if (!checkedReleaseGroups.has(value.id)) {
          // Add the projects of the release group into the options array.
          try {
            const resp = await props.fetchAllProjectsInReleaseGroup(value.id);
            resp.value?.data?.forEach((projectId: string) => {
              if (
                !options.some(opt => opt.value.type === 'project' && opt.value.id === projectId) &&
                !props.team.teamProjects?.some(teamProject => teamProject.projectId === projectId)
              ) {
                options.push({
                  label: projectId,
                  value: { type: 'project', id: projectId },
                });
              }
            });
          } catch (e) {
            // Error handled by Redux.
          }
        }
      }
    }
    setSelectedOptions(options);
    setCheckedReleaseGroups(newCheckedReleaseGroups);
  };

  const fetchingAllProjectsInGroup = props.fetchingAllProjectsInGroupStatus.loading;

  return (
    <Modal show onHide={props.onCancel} className="modal-add-team-projects">
      <Modal.Header closeButton>
        <Modal.Title>
          Add Projects to {props.team.name}
          {fetchingAllProjectsInGroup && (
            <Loader
              size="20px"
              style={{ display: 'inline-block', width: '20px', height: '20px', verticalAlign: 'text-bottom', marginLeft: '5px' }} />
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {hasError(props.status) && <ErrorAlert status={props.status} />}
        {hasError(props.fetchingAllProjectsInGroupStatus) && (
          <ErrorAlert status={props.fetchingAllProjectsInGroupStatus} />
        )}
        {props.status.loading
          ? (
            <Loader style={{ marginTop: '18px', marginBottom: '18px' }} />
            ) : (
              <Select
                isMulti
                name="projects"
                // @ts-expect-error It doesn't like the way we have two possible types for the value.
                options={modalOptions}
                onChange={updateSelectedOptions}
                value={selectedOptions}
              />
            )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="small"
            onClick={props.onCancel}
            isDisabled={props.status.loading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={() => filterProjectsAndReleaseGroups(selectedOptions)}
            isDisabled={props.status.loading || !selectedOptions.length}
          >
            Add
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = createStructuredSelector({
  status: TeamsModule.selectors.getTeamUpdatingStatus,
  fetchingAllProjectsInGroupStatus: ProjectGroupModule.selectors.getFetchingAllProjectsInGroupStatus,
});

const mapDispatchToProps: DispatchProps = {
  fetchAllProjectsInReleaseGroup: ProjectGroupModule.actions.fetchAllProjectsInReleaseGroup,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ModalAddTeamProjects);
